import { useState, useCallback, useMemo } from "react";
import { usePostMessageHandler } from "hooks/PostMessage/Handler";
import { postMessageTo } from "hooks/PostMessage/Utils";
import { ArrowNavigationAction } from "hooks/PostMessage/Actions";
import queryString from "data/queryString";
import { get } from "data/constants";

type ArrowNavigationReceiverProps = {
    handlePagination?: ((itemUri: string, index: number) => void) | null;
    handleDeletedWhileOpen?:
        | ((params: {
              itemUri: string | null;
              rowIndex: number;
              isNext: boolean;
              onSetActiveRowIndex: (index: number, isNext: boolean) => number;
              getActiveRowItemUri: (rowIndex: number) => string | undefined;
              minCount: number;
          }) => void)
        | null;
    activeRowIndex?: number;
};

type PaginationDataProps = {
    source: string;
    qsParam: string;
    currentIndex: number;
};

type PostMessageProps = {
    rows: string[];
    currentIndex: number;
    source?: string;
    qsParam?: string;
};

const useArrowNavigationTransmitter = (
    source: string,
    target: Window,
    targetData: any,
    setCurrentIndex: (index: number) => void,
    history: any
) =>
    usePostMessageHandler(
        useMemo(
            () => ({
                [ArrowNavigationAction.Ready]: (data: PaginationDataProps) =>
                    postMessageTo(
                        ArrowNavigationAction.Load,
                        targetData,
                        target
                    ),
                [ArrowNavigationAction.Paginate]: (
                    data: PaginationDataProps
                ) => {
                    if (source !== data.source) {
                        return;
                    }
                    const search = queryString.parse(window.location.search);
                    search[data.qsParam] = String(data.currentIndex);
                    setCurrentIndex(data.currentIndex);
                    const a = document.createElement("a"); // Use a tag for Location conversion
                    a.href = window.location.href;
                    // Remove basename from pathname to fix relative browsing
                    a.pathname = a.pathname.replace(
                        String(get(window.CONFIG, ["host", "basename"], "")),
                        ""
                    );
                    a.search = queryString.stringify(search);
                    history.push(a.pathname + a.search);
                },
            }),
            [source, target, targetData, setCurrentIndex, history]
        )
    );

const useArrowNavigationReceiver = ({
    handlePagination = null,
    handleDeletedWhileOpen = null,
    activeRowIndex: currentIndexFromProps = 0,
}: ArrowNavigationReceiverProps = {}) => {
    const [paginationEvents, setPaginationEvents] = useState<
        Record<string, () => Promise<boolean>>
    >({});
    const [postMessageProps, setPostMessageProps] = useState<PostMessageProps>({
        rows: [],
        currentIndex: currentIndexFromProps,
    });

    const { rows, currentIndex, source, qsParam } = postMessageProps;
    const [minCount, setMinCount] = useState(0);
    const [maxCount, setMaxCount] = useState(rows.length);

    const addPaginationEvent = useCallback(
        (id: string, event: () => Promise<boolean>) => {
            setPaginationEvents((prevEvents) => {
                prevEvents[id] = event;
                return prevEvents;
            });
        },
        []
    );

    const removePaginationEvent = useCallback((id: string) => {
        setPaginationEvents((prevEvents) => {
            delete prevEvents[id];
            return prevEvents;
        });
    }, []);

    const paginationEvent = useMemo(
        () => ({
            Add: addPaginationEvent,
            Remove: removePaginationEvent,
        }),
        [addPaginationEvent, removePaginationEvent]
    );

    const getActiveRowItemUri = useCallback(
        (rowIndex: number): string | undefined => {
            return rows?.[rowIndex] ?? undefined;
        },
        [rows]
    );

    const isNextDisabled = useMemo(
        (): boolean => currentIndex + 1 >= maxCount,
        [currentIndex, maxCount]
    );

    const isPrevDisabled = useMemo(
        (): boolean => currentIndex <= minCount,
        [currentIndex, minCount]
    );

    const getNext = useCallback(
        (index: number): number => {
            for (let i = index; i < maxCount; ++i) {
                /* istanbul ignore else */
                if (rows[i] != null) {
                    return i;
                }
            }
            return index - 1;
        },
        [rows, maxCount]
    );

    const getPrev = useCallback(
        (index: number): number => {
            for (let i = index; i >= minCount; --i) {
                /* istanbul ignore else */
                if (rows[i] != null) {
                    return i;
                }
            }
            return index + 1;
        },
        [rows, minCount]
    );

    const handlePaginationEvents = useCallback(async () => {
        const events = Object.values(paginationEvents);
        if (events.length > 0) {
            const executedEvents = events.map((f) => f());
            const result = await Promise.all(executedEvents);
            /* istanbul ignore else */
            if (result.some((result) => !result)) {
                console.log("Pagination prevented by registered event");
                return false;
            }
        }
        return true;
    }, [paginationEvents]);

    const onPaginate = useCallback(
        async (
            index: number,
            postMessage = false,
            ignorePaginationEvents = false
        ) => {
            if (
                ignorePaginationEvents === false &&
                (await handlePaginationEvents()) === false
            ) {
                return;
            }
            const itemUri = rows[index];
            if (itemUri == null) {
                return (
                    rows.length > 0 &&
                    typeof handleDeletedWhileOpen === "function" &&
                    handleDeletedWhileOpen({
                        itemUri,
                        rowIndex: index,
                        isNext: currentIndex < index,
                        onSetActiveRowIndex: /* istanbul ignore next */ (
                            index,
                            isNext
                        ) => (isNext ? getNext(index) : getPrev(index)),
                        getActiveRowItemUri,
                        minCount,
                    })
                );
            }
            typeof handlePagination === "function" &&
                handlePagination(rows[index], index);
            postMessage &&
                postMessageTo(
                    ArrowNavigationAction.Paginate,
                    {
                        source,
                        qsParam,
                        currentIndex: index,
                    },
                    /* istanbul ignore next */ window.opener || window
                );
        },
        [
            handlePaginationEvents,
            handlePagination,
            handleDeletedWhileOpen,
            currentIndex,
            qsParam,
            source,
            rows,
            getActiveRowItemUri,
            minCount,
            getNext,
            getPrev,
        ]
    );

    const onPrev = useCallback(async () => {
        /* istanbul ignore if */
        if (isPrevDisabled) return;
        if (false === (await handlePaginationEvents())) {
            return;
        }
        const prevRowIndex = getPrev(currentIndex - 1);
        if (prevRowIndex === currentIndex) {
            /* istanbul ignore else */
            if (minCount === 0 && minCount !== prevRowIndex)
                setMinCount(prevRowIndex);
        } else {
            setPostMessageProps((prevProps) => ({
                ...prevProps,
                currentIndex: prevRowIndex,
            }));
        }
        onPaginate(prevRowIndex, true, true);
    }, [
        handlePaginationEvents,
        getPrev,
        minCount,
        currentIndex,
        isPrevDisabled,
        onPaginate,
    ]);

    const onNext = useCallback(async () => {
        /* istanbul ignore if */
        if (isNextDisabled) return;
        if (false === (await handlePaginationEvents())) {
            return;
        }
        const nextRowIndex = getNext(currentIndex + 1);
        if (nextRowIndex === currentIndex) {
            /* istanbul ignore else */
            if (maxCount === rows.length && maxCount !== nextRowIndex + 1)
                setMaxCount(nextRowIndex + 1);
        } else {
            setPostMessageProps((prevProps) => ({
                ...prevProps,
                currentIndex: nextRowIndex,
            }));
        }
        onPaginate(nextRowIndex, true, true);
    }, [
        handlePaginationEvents,
        getNext,
        maxCount,
        rows.length,
        currentIndex,
        isNextDisabled,
        onPaginate,
    ]);

    usePostMessageHandler(
        useMemo(
            () => ({
                [ArrowNavigationAction.Load]: (data: {
                    rows: string[];
                    currentIndex: number;
                }) => {
                    /* istanbul ignore if */
                    if (source) {
                        return;
                    }
                    setPostMessageProps({
                        ...data,
                        rows: data.rows ?? [],
                        currentIndex: data.currentIndex ?? 0,
                    });
                    setMinCount(0);
                    setMaxCount(data.rows?.length ?? 0);
                },
                [ArrowNavigationAction.Reload]: (data: {
                    rows: string[];
                    currentIndex: number;
                    newSource: string;
                    source: string;
                }) => {
                    /* istanbul ignore next */
                    if (data.source !== source && !data.newSource) {
                        return;
                    }
                    const rows = data.rows ?? [];
                    let currentIndex = data.currentIndex ?? 0;
                    if (currentIndex >= rows.length) {
                        currentIndex = rows.length - 1;
                    }
                    setPostMessageProps((prevProps) => {
                        /* istanbul ignore else */
                        if (
                            prevProps.currentIndex !== currentIndex ||
                            data.newSource
                        ) {
                            onPaginate(currentIndex, false, true);
                        }
                        return {
                            ...prevProps,
                            rows,
                            currentIndex,
                            source: data.newSource ?? prevProps.source,
                        };
                    });
                    setMinCount(0);
                    setMaxCount(data.rows?.length ?? 0);
                },
                [ArrowNavigationAction.SetIndex]: (data: {
                    rows: string[];
                    currentIndex: number;
                    source: string;
                }) => {
                    if (
                        data.source !== source ||
                        data.currentIndex === currentIndex
                    ) {
                        return;
                    }
                    onPaginate(data.currentIndex);
                    setPostMessageProps((prevProps) => ({
                        ...prevProps,
                        currentIndex: data.currentIndex,
                    }));
                },
            }),
            [source, onPaginate, currentIndex]
        )
    );

    return useMemo(
        () => ({
            hasMoreThanOne: rows.length > 1,
            activeItemUri: getActiveRowItemUri(currentIndex),
            onNext,
            onPrev,
            isNextDisabled,
            isPrevDisabled,
            paginationEvent,
        }),
        [
            rows.length,
            currentIndex,
            onNext,
            onPrev,
            isNextDisabled,
            isPrevDisabled,
            getActiveRowItemUri,
            paginationEvent,
        ]
    );
};

export { useArrowNavigationTransmitter, useArrowNavigationReceiver };
